import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { ArrowRight, ExternalLink, HeartHandshake, Store, Users } from 'lucide-react';
import { useJsonLd } from '@/hooks/useJsonLd';

const KANIT = { fontFamily: "'Kanit', system-ui, sans-serif" };

const PARTNERSHIP_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Carolina Panthers Small Business Partner 2026 | KGOB CPAs & Advisors',
  url: 'https://kgob.com/panthers-partnership',
  description:
    'KGOB CPAs & Advisors is an official Carolina Panthers Small Business Partner for the 2026 season, celebrating and supporting small businesses across the Carolinas.',
  datePublished: '2026-08-10',
  about: {
    '@type': 'AccountingService',
    name: 'KGOB CPAs & Advisors',
    legalName: 'Kohari Gonzalez Oneyear & Brown PLLC',
    url: 'https://kgob.com',
    logo: 'https://kgob.com/brand/kgob-logo.png',
    memberOf: {
      '@type': 'Organization',
      name: 'Carolina Panthers Small Business Partner Program',
      url: 'https://www.panthers.com/about-us/smallbusinesspartners',
      parentOrganization: { '@type': 'SportsTeam', name: 'Carolina Panthers', url: 'https://www.panthers.com' },
    },
  },
};

const PILLARS = [
  {
    icon: Store,
    title: 'Championing small business',
    text: 'Small businesses are the backbone of the Carolinas, and they are the heart of our practice. This partnership lets us celebrate the owners who take the risks, sign the paychecks, and build our communities.',
  },
  {
    icon: HeartHandshake,
    title: 'A shared commitment',
    text: 'The Panthers Small Business Partner program recognizes local firms that invest in the region. We are proud to stand alongside the team in supporting the businesses that make Charlotte and the Carolinas thrive.',
  },
  {
    icon: Users,
    title: 'What it means for you',
    text: 'Nothing changes about how we serve you, and everything about why. The same partner-led tax, accounting, and advisory work, backed by a firm that shows up for its community all season long.',
  },
];

export default function PanthersPartnership() {
  useJsonLd('panthers-partnership', PARTNERSHIP_SCHEMA);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Carolina Panthers Small Business Partner 2026 | KGOB CPAs & Advisors</title>
        <meta
          name="description"
          content="KGOB CPAs & Advisors is an official Carolina Panthers Small Business Partner for the 2026 season, celebrating and supporting small businesses across the Carolinas."
        />
        <link rel="canonical" href="https://kgob.com/panthers-partnership" />
        <meta property="og:title" content="Carolina Panthers Small Business Partner 2026 | KGOB CPAs & Advisors" />
        <meta
          property="og:description"
          content="KGOB CPAs & Advisors is an official Carolina Panthers Small Business Partner for the 2026 season, celebrating and supporting small businesses across the Carolinas."
        />
        <meta property="og:url" content="https://kgob.com/panthers-partnership" />
        <meta property="og:type" content="website" />
      </Helmet>
      <Header />
      <main data-testid="panthers-partnership-page">
        {/* Hero */}
        <section className="border-b border-[#EAEFF4]">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[12px] font-semibold tracking-[2.5px] text-[#0085CA] mb-4" style={KANIT}>
                OFFICIAL ANNOUNCEMENT · 2026 SEASON
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.1] text-[#002C6B]" style={KANIT}>
                We've teamed up with the Carolina Panthers
              </h1>
              <p className="mt-6 text-base text-[#0B2240]/75 leading-relaxed max-w-xl">
                Kohari Gonzalez Oneyear &amp; Brown is an official Carolina Panthers Small Business
                Partner for the 2026 season. It is a partnership built on something we have always
                believed: the Carolinas run on small business, and small business deserves a team
                behind it.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="https://app.kgob.com/book-consultation?source=panthers-partnership"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#0085CA] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#002C6B]"
                  style={KANIT}
                  data-testid="panthers-page-book-cta"
                >
                  Book a Meeting <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="https://www.panthers.com/about-us/smallbusinesspartners"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#0085CA] transition-colors hover:text-[#002C6B]"
                  style={KANIT}
                  data-testid="panthers-page-external-link"
                >
                  See the program on panthers.com <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
            {/* Blue panel with seam, echoing the banner treatment */}
            <div
              className="relative h-[260px] sm:h-[300px] bg-[#101820] rounded-sm overflow-hidden"
              style={{ clipPath: 'polygon(0 24px, 100% 0, 100% 100%, 0 100%)' }}
              data-testid="panthers-page-hero-panel"
            >
              <div
                className="absolute inset-0 bg-[#0085CA] flex items-center justify-center px-8"
                style={{ clipPath: 'polygon(0 36px, 100% 10px, 100% 100%, 0 100%)' }}
              >
                <img
                  src="/brand/panthers/panthers-sbp-blue-cropped.jpg"
                  alt="Carolina Panthers Small Business Partner 2026"
                  className="max-h-[180px] w-auto max-w-full object-contain"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Pillars */}
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {PILLARS.map(({ icon: Icon, title, text }) => (
              <div key={title} data-testid={`panthers-pillar-${title.split(' ')[0].toLowerCase()}`}>
                <div className="w-11 h-11 rounded-lg bg-[#0085CA]/10 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#0085CA]" aria-hidden="true" />
                </div>
                <h2 className="text-base md:text-lg font-semibold text-[#002C6B] mb-2" style={KANIT}>
                  {title}
                </h2>
                <p className="text-sm text-[#0B2240]/70 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Closing CTA */}
        <section className="bg-[#0B2240]">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 lg:py-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <h2 className="text-base md:text-lg font-semibold text-white" style={KANIT}>
                Run a small business in the Carolinas?
              </h2>
              <p className="mt-2 text-sm text-white/70 max-w-lg">
                From tax planning to valuations to a mid-year health check, our partners work with
                owners at every stage. Let's talk about yours.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/services"
                className="inline-flex items-center gap-2 rounded-xl border border-white/25 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                style={KANIT}
                data-testid="panthers-page-services-link"
              >
                Explore Services
              </Link>
              <a
                href="https://app.kgob.com/book-consultation?source=panthers-partnership-footer"
                className="inline-flex items-center gap-2 rounded-xl bg-[#D4AF37] px-5 py-2.5 text-sm font-semibold text-[#0B2240] transition-colors hover:brightness-95"
                style={KANIT}
                data-testid="panthers-page-footer-book-cta"
              >
                Book a Meeting <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
